/**
 * Deterministic color assignment for technicians on the recall map.
 *
 * Technicians have no stored color (configurable colors are a future task), so a
 * color is derived at runtime from a curated palette. The palette deliberately
 * AVOIDS every color the map already uses to mean something, so a technician can
 * never be mistaken for another element:
 *   - manual stops      → sky blue   (#0ea5e9 / #0284c7 / #3b82f6)
 *   - "handled" ring    → emerald    (#10b981)
 *   - "past due"        → amber      (#f59e0b)
 *   - dealer bubbles    → bronze     (#7C5F2C / #d4854a)
 *   - errors            → red        (#ef4444)
 * Technician colors therefore live in the unused arc: purple / pink / teal /
 * indigo / fuchsia / rose. Color is a SECONDARY cue — the technician filter is
 * the primary way to isolate a technician's areas.
 */

/**
 * Curated, clash-free base palette (8 hues), ordered so sequential assignments
 * are maximally distinct. Each is saturated/dark enough to read as a filled
 * marker on light OSM tiles and in dark mode.
 */
// Selected by maximizing mutual ΔE while keeping every color ≥ΔE 24 from the
// reserved set (base min pairwise ΔE ≈ 17.9). Ordered so sequential assignments
// are maximally distinct (the order is the greedy max-min-distance sequence).
export const TECHNICIAN_PALETTE: string[] = [
  '#C026D3', // fuchsia
  '#0D9488', // teal
  '#BE185D', // deep pink
  '#4338CA', // indigo
  '#0E7490', // petrol
  '#9333EA', // purple
  '#EC4899', // pink
  '#8B5CF6' // violet
];

/** Stable string hash → index (fallback when roster index is unknown). */
const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0; // force 32-bit int
  }
  return Math.abs(hash);
};

const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
};

const hslToHex = (h: number, s: number, l: number): string => {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ln - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g] = [c, x];
  else if (h < 120) [r, g] = [x, c];
  else if (h < 180) [g, b] = [c, x];
  else if (h < 240) [g, b] = [x, c];
  else if (h < 300) [r, b] = [x, c];
  else [r, b] = [c, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Map any technician index to a color, scaling past the base palette without
 * cycling (identical colors) or drifting into reserved hues. The first 8 indices
 * use the curated palette; beyond that, the same safe HUES repeat with shifted
 * LIGHTNESS tiers (alternating lighter/darker, clamped to a legible band), so a
 * 15–20+ technician roster still gets distinct, clash-free colors.
 */
const L_MIN = 32;
const L_MAX = 68;
const L_BAND = L_MAX - L_MIN;
// Relative lightness offsets for overflow tiers. Applied as a fold into the
// legible band (not a clamp): this keeps each hue's variants distinct from one
// another AND preserves the lightness gap between different base hues, so close
// base hues never collapse onto the same color the way absolute targets would.
const TIER_OFFSETS = [16, -16, 32, -32];

export const colorForIndex = (index: number): string => {
  const len = TECHNICIAN_PALETTE.length;
  const base = TECHNICIAN_PALETTE[index % len] as string;
  const tier = Math.floor(index / len);
  if (tier === 0) return base;

  const { h, s, l } = hexToHsl(base);
  const offset = TIER_OFFSETS[(tier - 1) % TIER_OFFSETS.length] as number;
  // Fold (l + offset) into [L_MIN, L_MAX); hue + saturation stay put, so the
  // color can never drift into a reserved hue.
  const foldedL = L_MIN + ((((l + offset - L_MIN) % L_BAND) + L_BAND) % L_BAND);
  return hslToHex(h, s, foldedL);
};

/**
 * Return a stable, clash-free color for a technician.
 *
 * With the loaded roster, color is assigned by the technician's position in the
 * id-sorted roster (stable across renders). Off-roster, a hash of the id keeps it
 * deterministic. Both route through `colorForIndex`, so any roster size is covered.
 */
export const getTechnicianColor = (
  technicianId: string,
  technicians?: Array<{ id: string }>
): string => {
  if (technicians && technicians.length > 0) {
    const index = technicians
      .map(t => t.id)
      .sort()
      .indexOf(technicianId);
    if (index >= 0) return colorForIndex(index);
  }
  return colorForIndex(hashString(technicianId) % TECHNICIAN_PALETTE.length);
};
